import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dismissSmsAlerts,
  fetchPendingSmsAlerts,
  fetchReplyStatus,
  muteSmsAlerts,
  replyToInboundMessage,
  type SmsAlertMessage,
} from "../../api/inboundMessages";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../ui/modal";
import { formatPhone } from "../../lib/format";
import { usePollPacing, type PollPacingTiers } from "../../hooks/usePollPacing";
import { LINEUP_TENANT_ID } from "../auth/InboundMessagesRoute";
import { supabase } from "../../lib/supabase";
import { MaskedText } from "../../context/PrivacyMaskContext";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { rememberInboundDeviceFilter } from "../../lib/inboundDeviceFilter";
import { useUrlModal } from "../../hooks/useModal";
import StorePhoneLineStatusView, {
  phoneLineIsBlocked,
} from "../messaging/StorePhoneLineStatus";
import {
  rememberSendingDevice,
  toSendingDeviceOption,
  useSendingDevices,
  type SendingDeviceOption,
} from "../../hooks/useSendingDevices";
import { storePhoneLineStatus } from "../../../shared/store-phone-line";

const CONVERSATION_MODAL_PARAMS = ["messageId"] as const;

// 문자 알림 폴링 주기 — 사람이 지금 보고 있는지에 따라 4단계로 늦춘다(usePollPacing 헤더에 실측 근거).
// 요지: 48시간에 23,950 건(물량 1위, OPTIONS 포함 실왕복 두 배)이 4칸짜리 main DB 대기줄을 계속 두드려
// 저녁 5xx 스파이크의 배경 압력이 됐다. **멈추지는 않는다** — 숨은 탭 폴링이 데스크톱 알림을 먹여
// 살리므로, 멈추면 매장이 고객 문자를 놓친다. 단계는 전부 "늦추기"이고, 실제로 늦춰진 뒤(숨김 5분
// 초과) 탭으로 돌아오면 즉시 한 번 조회해 공백을 메운다.
//
// **주기표가 두 벌인 이유(티켓 #507)** — 실시간 신호(티켓 #444)가 붙어 있으면 새 문자는 1~2초 안에
// 신호로 오므로 폴링은 "신호가 유실됐을 때만 걸리는 그물"이다. 그물은 촘촘할 이유가 없다.
// 반대로 신호가 끊겨 있으면 폴링이 곧 유일한 통로라 촘촘해야 한다. 그래서 **연결 상태에 따라
// 표를 갈아끼운다** — 종전엔 신호가 살아 있든 죽었든 15초로 돌아, 하루 DB 시간의 9%를 썼다.
const POLL_TIERS_REALTIME_DOWN: PollPacingTiers = {
  // 보고 있고 방금까지 조작함 — 신호가 없으니 이때는 이 주기가 문자 인지 속도 그 자체다.
  active: 15_000,
  // 탭은 보이는데 10분 넘게 조작이 없음(자리 비움 추정). 카드는 화면에 남아 있으니 늦어도 안 놓친다.
  idle: 60_000,
  // 방금 다른 창·다른 프로그램으로 넘어갔다(5분 이내) — **원래 주기를 그대로 유지한다.**
  // 크롬이 숨은 탭 타이머를 분당 1회로 정렬하는 건 숨은 뒤 5분이 지나서부터라, 이 구간을 60초로
  // 내리면 "잠깐 다른 프로그램 보는 중"에 도착한 고객 문자의 데스크톱 알림이 실제로 15초 → 61초로
  // 늦어진다. 물량은 아래 두 단계에서 줄인다.
  hidden: 15_000,
  // 숨은 채로 5분 초과 — 여기서부터는 브라우저가 어차피 분당 1회로 정렬하므로 60초가 사실상 무손실.
  "hidden-throttled": 60_000,
  // 숨은 채로 30분 넘게 방치된 탭(밤새 켜둔 매장 PC) — 여기서 물량 대부분이 나왔다.
  "hidden-long": 180_000,
};

// 실시간 신호가 붙어 있는 평상시 — 새 문자·처리 표시는 신호로 즉시 오고, 이 폴링은 뒷받침이다.
// 신호를 놓쳤을 때의 최악 지연이 15초에서 3분으로 늘지만, 그 대신 상시 물량이 12배 줄어든다.
// (신호 유실 자체를 줄이는 몫은 서버가 맡는다 — broadcastTopicEvent 가 한 번 재시도한다.)
const POLL_TIERS_REALTIME_LIVE: PollPacingTiers = {
  active: 180_000,
  idle: 300_000,
  hidden: 180_000,
  "hidden-throttled": 300_000,
  "hidden-long": 600_000,
};

// 자리 비움·스로틀·장기 방치 기준.
//  - 자리 비움 10분: 3분은 "전화 한 통 받는 사이"에도 걸리는데, 폰보드를 보조 모니터에 띄워 둔 매장
//    구성에서는 탭이 계속 visible 이라 idle 로 내려가고 그러면 데스크톱 알림까지 최대 60초 늦는다.
//    하루 종일 방치되는 탭의 절감량은 문턱을 10분으로 늘려도 거의 그대로다.
//  - 숨김 5분: 크롬이 실제로 타이머를 늦추기 시작하는 시점과 맞춘 값.
const IDLE_AFTER_MS = 10 * 60_000;
const HIDDEN_THROTTLE_AFTER_MS = 5 * 60_000;
const HIDDEN_LONG_AFTER_MS = 30 * 60_000;

const RESULT_POLL_MS = 2_000;
const RESULT_POLL_LIMIT = 30;
// ── 카드 더미(접힘) ──
// 화면을 가리는 게 제일 큰 불만이었다: 문자가 여러 통 오면 카드가 위로 계속 쌓여 업무 화면을
// 덮었다. 그래서 **기본은 맨 위(가장 최근) 한 장만 실제로 보여주고**, 뒤에 남은 건 카드 모서리만
// 삐져나온 얇은 층으로 그린다. 한 장을 처리하면(✕·답장) 그 다음 장이 앞으로 나온다 — 어차피
// 한 장씩 처리하는 흐름이라 정보 손실이 없다.
// 뒤 층을 진짜 카드로 그리지 않는 게 핵심이다. 보이지도 않는 카드를 렌더하면 답장 입력칸·잠금
// 하트비트·타이머가 통째로 딸려 온다.
const STACK_PEEK_LAYERS = 2; // 접힘일 때 뒤로 보이는 장식 층 최대 개수
// 더미에 마우스를 올리면 뒤 카드가 "누구한테 온 문자인지" 한 줄씩 드러나고, 그 줄을 누르면
// 그 문자가 맨 앞으로 나온다 — 급한 문자를 먼저 처리하려고 앞 카드들을 닫을 필요가 없어진다.
// **펼치기 단추는 두지 않는다.** 마우스를 올리면 목록이 위로 자라 단추가 손 밑에서 밀려나므로
// 누를 수가 없었다(2026-08-19). 그래서 "올리면 열리고 치우면 닫힌다" 한 동작으로 합쳤고,
// 눌러야 하는 것([모두 닫기])은 마우스를 올려도 자리가 안 변하는 **카드 아래**로 내렸다.
// 다 보여 주지는 않는다 — 밤새 쌓인 30건을 다 펼치면 화면을 도로 덮는다.
const STACK_PREVIEW_LIMIT = 6;

// ── 실시간 신호 + 답장 잠금 (티켓 #444) ──
// 새 문자·처리 신호는 매장 전용 토픽(`tenant-sms:{매장}`) broadcast 로 즉시 온다(서버가 쏜다).
// 같은 토픽으로 "누가 이 카드에 답장을 쓰는 중인지"도 브라우저끼리 직접 주고받는다(DB 저장 없는
// 휘발성 신호 — form-presence 와 같은 방식). 위 폴링은 신호 유실 대비 뒷받침으로만 남는다.
const ALERTS_REFRESH_DEBOUNCE_MS = 400; // 문자함 묶음 업로드 등 신호 폭주를 재조회 한 번으로 합친다
const REPLY_HEARTBEAT_MS = 5_000; // 답장 작성 중 하트비트 — 입력마다 쏘지 않고 초안을 실어 5초 묶음
const REPLY_IDLE_RELEASE_MS = 30_000; // 작성자가 이만큼 무입력이면 다른 사람에게 [해제하고 이어쓰기]
const REPLY_PRESENCE_STALE_MS = 45_000; // 하트비트가 이만큼 끊기면 잠금 자체를 거둔다(탭 닫힘·PC 종료)
const SENT_NOTICE_CLOSE_MS = 3_000; // "○○ 님이 답장을 보냈습니다" 표시 후 카드가 닫히기까지
const FALLBACK_USER_NAME = "이름 미등록 직원"; // 계정 이름이 비어 있으면 문구에 이렇게 나온다

/** 다른 사람이 이 알림에 답장을 쓰고 있다는 휘발성 신호(다른 브라우저에서 온 것만 담는다). */
type ReplyPresence = {
  alertId: number;
  userId: string;
  name: string;
  /** 작성 중이던 초안 — [해제하고 이어쓰기]가 이 글자를 그대로 물려받는다. */
  draft: string;
  /** 보낸 쪽 기준 "마지막 입력 후 경과(ms)" — PC 시계가 서로 달라도 안전하게 상대시간만 주고받는다. */
  idleMs: number;
  /** [해제하고 이어쓰기]로 넘겨받은 경우, 원래 쓰던 사람의 id. */
  takenFrom: string | null;
  /** 이 브라우저가 신호를 받은 시각(로컬 시계). */
  receivedAt: number;
};

// 데스크톱 알림 권유를 닫으면 다시 조르지 않는다(브라우저별로 기억).
const NOTI_ASK_DISMISSED_KEY = "inbound.sms.notiAskDismissed";

type CardState = "idle" | "sending" | "sent" | "failed";

/** 브라우저가 데스크톱 알림을 지원하나 — 사파리 구버전·비HTTPS 환경 방어. */
function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

/**
 * 번호 비교·표시의 표준형(숫자만, +82 → 0). 폰이 국제형식으로 올려주는 경우가 있어
 * (운영 원장에 실제로 있다) 국내형으로 되돌린다 — 서버의 canonicalPhone 과 같은 규칙이다.
 */
function toLocalPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  return digits.startsWith("82") && digits.length >= 11 ? `0${digits.slice(2)}` : digits;
}

function samePhone(a: string, b: string): boolean {
  return toLocalPhone(a) === toLocalPhone(b);
}

/**
 * 화면에 보여줄 발신번호 — 모르는 번호도 최소한 번호는 나오게 한다.
 * 표준형으로 되돌린 뒤 포맷한다. 안 그러면 "821-0403-1320" 처럼 읽을 수 없는 번호가 뜬다.
 */
function phoneLabel(message: SmsAlertMessage): string {
  return formatPhone(toLocalPhone(message.sender)) || message.sender || "발신자 미상";
}

/**
 * 폰보드 화면을 안 보고 있을 때만 데스크톱 알림을 띄운다.
 * 탭이 숨겨졌거나(다른 탭) 창이 포커스를 잃었으면(다른 프로그램) 화면 속 카드는 안 보이므로,
 * 그때만 OS 알림으로 대신 알린다 — 보고 있는데 둘 다 뜨면 방해만 된다.
 */
function notifyIfAway(message: SmsAlertMessage) {
  if (!notificationsSupported() || Notification.permission !== "granted") return;
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  try {
    // 우리 고객이면 제목에 이름을 세운다 — OS 알림 목록에서는 제목이 먼저 보이므로,
    // 번호만 떠 있을 때보다 "누구 문자인지" 판단이 훨씬 빠르다.
    const title = message.customerName
      ? `새 문자 · ${message.customerName}`
      : "새 문자 도착";
    const noti = new Notification(title, {
      body: `${phoneLabel(message)}\n${message.body.slice(0, 120)}`,
      tag: `inbound-sms-${message.id}`, // 같은 문자로 두 번 안 뜨게
      requireInteraction: false,
    });
    noti.onclick = () => {
      window.focus();
      noti.close();
    };
  } catch {
    // 알림 생성 실패는 조용히 넘긴다 — 화면 속 카드는 그대로 뜬다.
  }
}

function receivedLabel(ts: string): string {
  try {
    return new Date(ts).toLocaleString("ko-KR", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

function SmsCard({
  message,
  me,
  lock,
  sentBy,
  now,
  onClose,
  onOpen,
  onHandled,
  onMuted,
  onSendPresence,
  onRelease,
  onSent,
  onTakeover,
  sendingDevices,
}: {
  message: SmsAlertMessage;
  /** 앱 셸에서 한 번 받아 둔 발송폰 목록 — 카드가 뜰 때마다 다시 조회하지 않는다. */
  sendingDevices: SendingDeviceOption[];
  /** 문구("○○ 님이 답장 중입니다")와 잠금 판정에 쓰는 내 계정. */
  me: { id: string; name: string };
  /** 다른 사람이 이 카드에 답장을 쓰는 중이라는 신호(없으면 null). */
  lock: ReplyPresence | null;
  /** 다른 사람이 방금 답장을 보냈으면 그 사람 이름 — 잠깐 보여주고 부모가 카드를 닫는다. */
  sentBy: string | null;
  /** 부모가 3초마다 갱신하는 현재 시각 — 잠금 경과 판정용. */
  now: number;
  onClose: () => void;
  /** 수신 내역을 이 문자를 받은 폰 기준으로 열기. 카드 자체는 미처리 상태로 그대로 둔다. */
  onOpen: () => void;
  /** 답장이 실제로 나간 순간 — 카드가 3초 뒤 닫히기 전에 "처리됨"을 먼저 서버에 남긴다(티켓 #431) */
  onHandled: () => void;
  onMuted: (phone: string) => void;
  /** "내가 쓰는 중" 하트비트 발신(5초 간격, 초안 동봉). */
  onSendPresence: (draft: string, idleMs: number, takenFrom: string | null) => void;
  /** 쓰다 만 잠금을 놓았다(초안 전부 지움·카드 닫음). */
  onRelease: () => void;
  /** 답장이 나갔다 — 다른 화면 카드에 "보냈습니다"를 띄우고 닫게 한다. */
  onSent: () => void;
  /** [해제하고 이어쓰기] — 부모가 이 카드의 잠금 신호를 지운다(이제 내가 작성자). */
  onTakeover: () => void;
}) {
  const [body, setBody] = useState("");
  const [state, setState] = useState<CardState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [sendingDevicePickerOpen, setSendingDevicePickerOpen] = useState(false);
  const [selectedSendingDeviceId, setSelectedSendingDeviceId] = useState(
    () => message.replyDeviceId,
  );
  // "내가 이 카드의 작성자"인 동안 true — 하트비트가 돌고, 다른 사람 카드는 잠긴다.
  const [holding, setHolding] = useState(false);
  const holdingRef = useRef(false);
  const bodyValueRef = useRef("");
  const lastInputRef = useRef(0);
  const takenFromRef = useRef<string | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 실수로 눌러 알림이 조용히 꺼지면 놓친 줄도 모르므로 확인 모달로 한 번 되묻는다.
  const [muteState, setMuteState] = useState<"idle" | "confirm" | "working">("idle");
  // 끄기 실패는 답장 실패(error)와 따로 둔다 — 답장 영역은 state 가 "failed" 일 때만 그려져서,
  // 같은 칸을 쓰면 끄기 실패 메시지가 아무 데도 안 뜬다.
  const [muteError, setMuteError] = useState<string | null>(null);

  async function mute() {
    setMuteState("working");
    setMuteError(null);
    try {
      const { phone } = await muteSmsAlerts(message.sender);
      onMuted(phone);
    } catch (e) {
      setMuteError(e instanceof Error ? e.message : "알림을 끄지 못했습니다");
      setMuteState("confirm");
    }
  }

  useEffect(() => {
    if (state !== "sent") return;
    const timer = setTimeout(onClose, 3_000);
    return () => clearTimeout(timer);
  }, [onClose, state]);

  // ── 답장 잠금(티켓 #444): 내가 쓰기 시작하면 하트비트로 알리고, 멈추면 놓는다 ──
  const stopHolding = useCallback(
    (notify: boolean) => {
      if (!holdingRef.current) return;
      holdingRef.current = false;
      setHolding(false);
      takenFromRef.current = null;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (notify) onRelease();
    },
    [onRelease],
  );

  const sendHeartbeat = useCallback(() => {
    onSendPresence(
      bodyValueRef.current,
      Math.max(0, Date.now() - lastInputRef.current),
      takenFromRef.current,
    );
  }, [onSendPresence]);

  const startHolding = useCallback(() => {
    if (holdingRef.current) return;
    holdingRef.current = true;
    setHolding(true);
    sendHeartbeat(); // 쓰기 시작을 즉시 알린다 — 이후는 5초 간격으로만(입력마다 쏘지 않는다)
    heartbeatRef.current = setInterval(sendHeartbeat, REPLY_HEARTBEAT_MS);
  }, [sendHeartbeat]);

  // 카드가 닫히면(언마운트) 쥐고 있던 잠금을 놓는다 — 안 놓으면 다른 사람이 30초를 기다린다.
  useEffect(() => () => stopHolding(true), [stopHolding]);

  // 잠금이 남에게 넘어갔거나(이어쓰기), 둘이 거의 동시에 쓰기 시작한 경합이면 내가 물러난다.
  // 경합의 승자는 id 사전순 — 양쪽이 같은 규칙을 쓰므로 반드시 한 명만 남는다.
  useEffect(() => {
    if (!lock || !holdingRef.current) return;
    if (lock.takenFrom === me.id || lock.userId < me.id) stopHolding(false);
  }, [lock, me.id, stopHolding]);

  function handleBodyChange(text: string) {
    setBody(text);
    bodyValueRef.current = text;
    lastInputRef.current = Date.now();
    if (text.trim() === "") {
      stopHolding(true); // 다 지웠으면 잠금을 놓는다 — 다른 사람이 바로 쓸 수 있게
    } else {
      startHolding();
    }
  }

  // 다른 사람 잠금 판정 — 내가 작성자면(경합에서 이긴 쪽 포함) 내 카드는 잠기지 않는다.
  const lockView =
    lock && !holding
      ? lock.takenFrom === me.id
        ? { kind: "takenover" as const, name: lock.name }
        : now - lock.receivedAt + lock.idleMs >= REPLY_IDLE_RELEASE_MS
          ? { kind: "stalled" as const, name: lock.name }
          : { kind: "writing" as const, name: lock.name }
      : null;
  const selectedSendingDevice =
    sendingDevices.find((device) => device.id === selectedSendingDeviceId) ??
    sendingDevices.find((device) => device.id === message.replyDeviceId) ??
    sendingDevices[0] ??
    null;
  const manuallySelectedDifferentDevice =
    selectedSendingDevice != null && selectedSendingDevice.id !== message.replyDeviceId;
  const phoneLineBlocked = selectedSendingDevice
    ? phoneLineIsBlocked(
        selectedSendingDevice.phoneNumber,
        selectedSendingDevice.reportedPhoneNumber,
        selectedSendingDevice.manuallyVerifiedPhoneNumber,
      )
    : true;
  // 선택폰이 있으면 null도 그 폰의 실제 상태다. 값마다 `??`로 원래 수신폰에 돌아가면
  // 번호 미보고·대표 확인 폰을 고른 순간 두 폰의 값이 다시 섞인다(티켓 #701).
  const selectedRegisteredPhoneNumber = selectedSendingDevice
    ? selectedSendingDevice.phoneNumber
    : message.receivingPhoneNumber;
  const selectedReportedPhoneNumber = selectedSendingDevice
    ? selectedSendingDevice.reportedPhoneNumber
    : message.replyPhoneNumber;
  const selectedManuallyVerifiedPhoneNumber = selectedSendingDevice
    ? selectedSendingDevice.manuallyVerifiedPhoneNumber
    : message.manuallyVerifiedPhoneNumber;
  const replyLineStatus = storePhoneLineStatus(
    selectedRegisteredPhoneNumber,
    selectedReportedPhoneNumber,
    selectedManuallyVerifiedPhoneNumber,
  );
  const replyLineCheck =
    replyLineStatus === "match"
      ? " ✓ 일치"
      : replyLineStatus === "manually_verified"
        ? " ✓ 대표 확인"
        : "";

  function selectSendingDevice(deviceId: string) {
    setSelectedSendingDeviceId(deviceId);
    rememberSendingDevice(deviceId);
    setSendingDevicePickerOpen(false);
  }

  function takeOver() {
    if (!lock) return;
    const inherited = lock.draft;
    setBody(inherited);
    bodyValueRef.current = inherited;
    lastInputRef.current = Date.now();
    takenFromRef.current = lock.userId;
    onTakeover(); // 부모가 이 카드의 잠금 신호를 지운다 — 지금부터 작성자는 나
    startHolding(); // 즉시 하트비트(takenFrom 포함) — 원래 쓰던 사람 화면이 그 신호로 잠긴다
  }

  async function sendReply() {
    const text = body.trim();
    if (!text || state !== "idle" || lockView || phoneLineBlocked) return;
    setState("sending");
    setError(null);
    try {
      const { queueId } = await replyToInboundMessage(
        message.id,
        text,
        selectedSendingDevice?.id,
      );
      for (let attempt = 0; attempt < RESULT_POLL_LIMIT; attempt += 1) {
        // 발송 결과를 기다리는 동안에도 "입력 중"으로 취급한다 — 발송이 길어져도(최대 60초)
        // 다른 사람 카드에 [해제하고 이어쓰기]가 떠서 이중 발송으로 이어지지 않게.
        lastInputRef.current = Date.now();
        const result = await fetchReplyStatus(queueId);
        if (result.status === "sent") {
          setState("sent");
          stopHolding(false);
          onSent(); // 다른 화면 카드: "○○ 님이 답장을 보냈습니다" → 함께 닫힘
          onHandled();
          return;
        }
        if (["failed", "canceled", "missing"].includes(result.status)) {
          setError(result.error);
          setState("failed");
          stopHolding(true);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, RESULT_POLL_MS));
      }
      setError("발송기 응답 시간이 지났습니다");
      setState("failed");
      stopHolding(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "답장을 보내지 못했습니다");
      setState("failed");
      stopHolding(true);
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-lg dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-brand-600 dark:text-brand-400">
            새 문자 도착
          </p>
          {message.customerName ? (
            <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
              <MaskedText value={message.customerName} kind="name" />
            </p>
          ) : null}
          <p className="text-[11px] text-gray-400">
            문자온 고객번호 <MaskedText value={phoneLabel(message)} kind="phone" /> ·{" "}
            {receivedLabel(message.receivedAt)}
          </p>
        </div>
        {/* 광고·스팸처럼 계속 뜰 필요 없는 번호를 매장이 직접 끈다. 문자 수신 자체는 그대로다.
            ✕ 바로 왼쪽에 두되, 실수로 눌러도 바로 꺼지지 않게 확인 모달을 한 번 거친다. */}
        <button
          type="button"
          onClick={() => setMuteState("confirm")}
          disabled={muteState === "working"}
          aria-label="이 번호 알림 그만 받기"
          title="이 번호 알림 그만 받기"
          className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-300"
        >
          <BellOffIcon />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="문자 알림 닫기"
          className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <span aria-hidden>✕</span>
        </button>
      </div>

      <p className="mt-2 line-clamp-4 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {message.body}
      </p>

      <button
        type="button"
        onClick={onOpen}
        className="mt-2 text-xs font-semibold text-brand-600 underline-offset-2 hover:underline dark:text-brand-400"
      >
        대화창으로 열기
      </button>

      <div className="relative mt-3 space-y-0.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
        <p>
          메시지 수신번호 {" "}
          {formatPhone(selectedRegisteredPhoneNumber ?? "") ||
            selectedSendingDevice?.fallbackName ||
            message.receivingDeviceName}
        </p>
        {manuallySelectedDifferentDevice ? (
          <p
            data-testid="sms-toast-origin-phone-number"
            className="text-xs font-medium text-gray-500 dark:text-gray-400"
          >
            문자가 들어온 폰 {" "}
            {formatPhone(message.receivingPhoneNumber ?? "") || message.receivingDeviceName}
          </p>
        ) : null}
        {selectedSendingDevice && sendingDevices.length > 1 ? (
          <button
            type="button"
            data-testid="sms-toast-sending-device-picker"
            aria-expanded={sendingDevicePickerOpen}
            onClick={() => setSendingDevicePickerOpen((open) => !open)}
            className="block rounded-md text-left text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-800 dark:text-emerald-300"
          >
            답장 발신번호 {selectedSendingDevice.display}{replyLineCheck} ▾ · 눌러서 다른 폰으로 바꾸기
          </button>
        ) : (
          <p>
            답장 발신번호 {selectedSendingDevice?.display ?? (formatPhone(message.replyPhoneNumber ?? "") || "확인 안 됨")}{replyLineCheck}
          </p>
        )}
        {sendingDevicePickerOpen && selectedSendingDevice && sendingDevices.length > 1 ? (
          <div
            role="listbox"
            aria-label="답장 발신번호 선택"
            className="absolute left-0 top-full z-20 mt-1 min-w-full overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-900"
          >
            {sendingDevices.map((deviceOption) => (
              <button
                key={deviceOption.id}
                type="button"
                role="option"
                aria-selected={deviceOption.id === selectedSendingDevice.id}
                onClick={() => selectSendingDevice(deviceOption.id)}
                className="flex w-full items-center justify-between gap-3 whitespace-nowrap px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <span>{deviceOption.display}</span>
                {deviceOption.id === selectedSendingDevice.id ? <span aria-hidden>✓</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        <StorePhoneLineStatusView
          mode="reply"
          registeredPhoneNumber={selectedRegisteredPhoneNumber}
          reportedPhoneNumber={selectedReportedPhoneNumber}
          manuallyVerifiedPhoneNumber={selectedManuallyVerifiedPhoneNumber}
          replyOriginPhoneNumber={
            manuallySelectedDifferentDevice ? message.receivingPhoneNumber : null
          }
          fallbackName={message.receivingDeviceName}
          showPrimaryLine={false}
        />
      </div>

      {sentBy ? (
        // 다른 사람이 방금 이 문자에 답장을 보냈다 — 잠깐 알리고 부모가 카드를 닫는다.
        <p className="mt-3 rounded-lg bg-emerald-50 px-2.5 py-2 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          {sentBy} 님이 답장을 보냈습니다
        </p>
      ) : !selectedSendingDevice ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
          이 폰은 답장 발송기가 연결돼 있지 않아요
        </p>
      ) : state === "sent" ? (
        <p className="mt-3 rounded-lg bg-emerald-50 px-2.5 py-2 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          보냈어요
        </p>
      ) : state === "failed" ? (
        <div className="mt-3 rounded-lg bg-rose-50 px-2.5 py-2 dark:bg-rose-500/10">
          <p className="text-xs font-semibold text-rose-600 dark:text-rose-300">보내지 못했어요</p>
          {error && <p className="mt-0.5 text-[11px] text-rose-500">{error}</p>}
        </div>
      ) : (
        <>
          {lockView?.kind === "writing" && (
            <p className="mt-3 rounded-lg bg-gray-100 px-2.5 py-2 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {lockView.name} 님이 답장 중입니다
            </p>
          )}
          {lockView?.kind === "takenover" && (
            <p className="mt-3 rounded-lg bg-gray-100 px-2.5 py-2 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {lockView.name} 님이 이어서 쓰고 있습니다
            </p>
          )}
          {lockView?.kind === "stalled" && (
            <div className="mt-3 rounded-lg bg-amber-50 px-2.5 py-2 dark:bg-amber-500/10">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                {lockView.name} 님이 쓰다가 멈췄습니다. 해제하고 이어서 쓰시겠어요?
              </p>
              <button
                type="button"
                onClick={takeOver}
                className="mt-2 w-full rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-600"
              >
                해제하고 이어쓰기
              </button>
            </div>
          )}
          <div className="mt-3 flex items-end gap-2">
            <textarea
              value={body}
              onChange={(e) => handleBodyChange(e.target.value)}
              rows={2}
              placeholder={lockView ? "다른 분이 답장을 쓰고 있어요" : "답장을 입력하세요"}
              disabled={state === "sending" || lockView != null}
              className="min-h-16 min-w-0 flex-1 resize-none rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-800 outline-none transition focus:border-brand-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white/90"
            />
            <button
              type="button"
              onClick={() => void sendReply()}
              disabled={
                !body.trim() || state === "sending" || lockView != null || phoneLineBlocked
              }
              className="h-9 shrink-0 rounded-lg bg-brand-500 px-3 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:opacity-40"
            >
              {state === "sending" ? "보내는 중…" : "보내기"}
            </button>
          </div>
        </>
      )}

      {/* 확인 모달 — 카드(z-99998) 위에 뜬다(공용 Modal 기본 z-99999). 끄는 중에도 열어 둔 채
          진행 상태를 보여준다: 카드가 사라지는 건 서버가 그 번호 알림을 지운 뒤다. */}
      {/* 컨벤션 예외 — "이 번호 알림을 끌까요?"를 묻고 끄는 동안 진행 상태를 보여주는
          확인창이다. 진행 중 상태라 링크로 다시 열 대상이 아니다. */}
      {/* eslint-disable-next-line modal-url/prefer-url-modal -- 위 주석 참고(확인창 예외) */}
      <Modal isOpen={muteState !== "idle"}
        onClose={() => muteState !== "working" && setMuteState("idle")}
        showCloseButton={false}
        className="max-w-[24rem] p-5"
      >
        <p className="text-base font-semibold text-gray-800 dark:text-white/90">
          이 번호 알림 그만 받을까요?
        </p>
        <p className="mt-1 text-sm font-semibold text-gray-600 dark:text-gray-300">
          {message.customerName ? (
            <>
              <MaskedText value={message.customerName} kind="name" /> ·{" "}
              <MaskedText value={phoneLabel(message)} kind="phone" />
            </>
          ) : (
            <MaskedText value={phoneLabel(message)} kind="phone" />
          )}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          앞으로 이 번호는 알림이 뜨지 않습니다. 문자는 계속 받아 수신 메시지에 쌓이고,
          언제든 다시 켤 수 있습니다.
        </p>
        {muteError && (
          <p className="mt-3 rounded-lg bg-rose-50 px-2.5 py-2 text-xs text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
            {muteError}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setMuteState("idle")}
            disabled={muteState === "working"}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-500 transition hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-gray-800"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => void mute()}
            disabled={muteState === "working"}
            className="rounded-lg bg-gray-700 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-40 dark:bg-gray-600 dark:hover:bg-gray-500"
          >
            {muteState === "working" ? "끄는 중…" : "알림 끄기"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

/** 종에 사선 — "이 번호 알림 그만 받기" 버튼용(아이콘 세트에 없어 여기 둔다). */
function BellOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <path d="M6 8c0 7-3 9-3 9h15" />
      <path d="M10.3 21a2 2 0 0 0 3.4 0" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

/**
 * 데스크톱 알림 권유 카드 — 권한이 아직 "물어보지 않음"일 때만 한 번 뜬다.
 * 브라우저가 권한 요청 창을 사용자 클릭에서만 안정적으로 띄우므로(파이어폭스는 필수),
 * 자동 호출하지 않고 버튼을 눌러야 요청한다.
 */
function NotificationPrompt({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<"idle" | "asking" | "denied">("idle");

  async function ask() {
    setState("asking");
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        onDone();
      } else if (permission === "denied") {
        setState("denied");
      }
    } catch {
      setState("idle");
    }
  }

  const description =
    state === "asking"
      ? "브라우저 위쪽에 뜬 창에서 [허용]을 눌러 주세요"
      : state === "denied"
        ? "지금은 알림이 차단돼 있어요. 주소창 오른쪽 끝의 종 모양을 눌러 '허용'으로 바꾸면 켜집니다"
        : "웹을 닫아도 문자는 서버에 보관돼 다음에 열면 모두 보입니다. 이 기능을 켜 두면 웹이 열린 상태에서 다른 프로그램을 쓰는 중에도 새 문자가 화면 구석에 뜹니다.";

  return (
    <div className="rounded-2xl border border-brand-200 bg-white p-3.5 shadow-lg dark:border-brand-500/30 dark:bg-gray-900">
      <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
        다른 창을 봐도 문자 알림 받기
      </p>
      <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
        {description}
      </p>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          나중에
        </button>
        <button
          type="button"
          onClick={() => void ask()}
          disabled={state !== "idle"}
          className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
        >
          알림 켜기
        </button>
      </div>
    </div>
  );
}

export default function NewSmsToastStack() {
  const { profile, hasHqDelegation } = useAuth();
  const conversationModal = useUrlModal("sms-conversation", CONVERSATION_MODAL_PARAMS);
  // 본사 수신 문자를 볼 수 있는 사람에게만 띄운다 — 서버 requireInboundMessageAccess 와
  // 같은 판정(2026-08-28 부터 라인업 매장 번호가 아니라 본사 위임 권한으로 가른다).
  // 직원(tenant)에게도 띄우려면 그 직원에게 hq_shared 쓰기를 주면 된다(티켓 #444 요구는
  // 「라인업 사람이면 직원도」였고, 지금은 「위임받았으면 누구든」으로 일반화됐다).
  const allowed = hasHqDelegation();
  const { devices: cachedSendingDevices } = useSendingDevices(allowed);
  const sendingDevices = cachedSendingDevices.map(toSendingDeviceOption);
  const alertScopeKey =
    profile?.viewingTenantId ?? profile?.tenantId ?? LINEUP_TENANT_ID;
  const myId = profile?.id ?? "";
  const myName = profile?.displayName?.trim() || FALLBACK_USER_NAME;
  const loadedOnceRef = useRef(false);
  const seenAlertIdsRef = useRef(new Set<number>());
  const [cards, setCards] = useState<SmsAlertMessage[]>([]);
  // 더미에 마우스가 올라가 있나 — 뒤 카드 미리보기를 여는 유일한 신호(단추가 없다).
  const [stackHovered, setStackHovered] = useState(false);
  // 사람이 뒤에서 끄집어내 맨 앞에 세운 문자. 그 카드가 처리돼 사라지면 자동으로 최신 문자로 돌아간다.
  const [frontAlertId, setFrontAlertId] = useState<number | null>(null);
  // alertId → 다른 사람의 "답장 작성 중" 신호. 하트비트가 끊기면 tick 이 걷어낸다.
  const [presence, setPresence] = useState<Record<number, ReplyPresence>>({});
  // alertId → 방금 답장을 보낸 사람 이름. 잠깐 보여주고 카드를 닫는다.
  const [sentNotices, setSentNotices] = useState<Record<number, string>>({});
  const sentTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const channelRef = useRef<RealtimeChannel | null>(null);
  // 실시간 신호가 지금 실제로 붙어 있나 — 이 값 하나가 폴링 주기표를 고른다(티켓 #507).
  // 붙어 있는 동안만 느린 표를 쓰고, 끊기면(인터넷 단절·서버 재기동·웹소켓 사망) 곧바로
  // 촘촘한 표로 되돌아간다. 처음에는 false 로 시작한다 — 아직 붙었다는 증거가 없기 때문이고,
  // 그래야 접속에 실패하는 환경에서 "빠른 폴링"이 기본값으로 남는다.
  const [realtimeLive, setRealtimeLive] = useState(false);
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 잠금 경과("30초 무입력") 판정용 시계 — 잠금이 있을 때만 3초 간격으로 돈다.
  const [nowTick, setNowTick] = useState(() => Date.now());

  const [askNoti, setAskNoti] = useState(false);

  const qc = useQueryClient();

  // 숨어 있던 탭이 다시 보이는 순간 즉시 한 번 조회한다 — 늦춘 주기 동안 도착한 문자를 바로 받는다.
  // (전역 default 가 refetchOnWindowFocus:false 라 react-query 가 알아서 해주지 않는다. 그리고
  //  "포커스"가 아니라 "탭이 보이나"로 판정해야 창을 여러 개 띄운 매장 PC 에서 정확하다.)
  // 키를 앞부분만 주는 이유: 계정·조회 매장이 바뀌어 키 뒷부분이 달라져도 지금 살아 있는(active)
  // 알림 쿼리를 놓치지 않고 잡는다.
  const refetchNow = useCallback(() => {
    void qc.refetchQueries({
      queryKey: ["inbound-messages", "pending-sms-alerts"],
      type: "active",
    });
  }, [qc]);

  const pollMs = usePollPacing(realtimeLive ? POLL_TIERS_REALTIME_LIVE : POLL_TIERS_REALTIME_DOWN, {
    enabled: allowed,
    idleAfterMs: IDLE_AFTER_MS,
    hiddenThrottleAfterMs: HIDDEN_THROTTLE_AFTER_MS,
    hiddenLongAfterMs: HIDDEN_LONG_AFTER_MS,
    onResume: refetchNow,
    // 5분 넘게 숨어 있었을 때만 복귀 즉시조회 — 그 전까지는 주기를 늦추지도 않았으니 메울 공백이
    // 없고, 짧은 탭 왕복마다 조회하면 요청량이 기존보다 늘어난다(기본값 tiers.active=15초보다 더
    // 보수적으로, 실제로 늦춰지기 시작하는 시점에 맞춘다).
    resumeMinHiddenMs: HIDDEN_THROTTLE_AFTER_MS,
  });

  const query = useQuery({
    queryKey: [
      "inbound-messages",
      "pending-sms-alerts",
      profile?.id ?? "anonymous",
      alertScopeKey,
    ],
    queryFn: fetchPendingSmsAlerts,
    enabled: allowed,
    refetchInterval: pollMs,
    // 다른 탭·다른 프로그램을 보는 동안에도 계속 확인해야 데스크톱 알림을 띄울 수 있다 — 그래서
    // 숨은 탭에서도 끄지 않고 주기만 늦춘다(POLL_TIERS.hidden / hidden-long).
    refetchIntervalInBackground: true,
  });

  // 신호가 몰려올 때(문자함 묶음 업로드 등) 재조회를 한 번으로 합친다.
  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) return;
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      refetchNow();
    }, ALERTS_REFRESH_DEBOUNCE_MS);
  }, [refetchNow]);

  // ── 실시간 채널(티켓 #444): 새 문자·처리 신호 수신 + 답장 잠금 신호 송수신 ──
  // 토픽을 `tenant:{매장}` 이 아니라 `tenant-sms:{매장}` 으로 분리한 이유: supabase-js 는 같은
  // 토픽 채널을 컴포넌트끼리 공유(dedupe)해서, CRM 화면이 `tenant:{매장}` 구독을 해제할 때
  // 이 카드의 구독까지 끊길 수 있다. 이 토픽은 AppLayout 상주인 이 컴포넌트만 소유한다.
  useEffect(() => {
    if (!allowed || !myId) return;
    const sentTimers = sentTimersRef.current;
    const channel = supabase.channel(`tenant-sms:${alertScopeKey}`);
    channel
      .on("broadcast", { event: "sms-alerts-changed" }, () => scheduleRefetch())
      .on("broadcast", { event: "sms-reply-presence" }, (msg) => {
        const p = (msg.payload ?? {}) as Partial<ReplyPresence>;
        const alertId = Number(p.alertId);
        if (!alertId || typeof p.userId !== "string" || p.userId === myId) return;
        const userId = p.userId;
        setNowTick(Date.now());
        setPresence((current) => ({
          ...current,
          [alertId]: {
            alertId,
            userId,
            name: typeof p.name === "string" && p.name.trim() ? p.name : FALLBACK_USER_NAME,
            draft: typeof p.draft === "string" ? p.draft : "",
            idleMs: Math.max(0, Number(p.idleMs) || 0),
            takenFrom: typeof p.takenFrom === "string" ? p.takenFrom : null,
            receivedAt: Date.now(),
          },
        }));
      })
      .on("broadcast", { event: "sms-reply-release" }, (msg) => {
        const p = (msg.payload ?? {}) as { alertId?: unknown; userId?: unknown };
        const alertId = Number(p.alertId);
        if (!alertId) return;
        setPresence((current) => {
          if (!current[alertId] || current[alertId].userId !== p.userId) return current;
          const next = { ...current };
          delete next[alertId];
          return next;
        });
      })
      .on("broadcast", { event: "sms-reply-sent" }, (msg) => {
        const p = (msg.payload ?? {}) as { alertId?: unknown; userId?: unknown; name?: unknown };
        const alertId = Number(p.alertId);
        if (!alertId || p.userId === myId) return;
        setPresence((current) => {
          if (!current[alertId]) return current;
          const next = { ...current };
          delete next[alertId];
          return next;
        });
        // "○○ 님이 답장을 보냈습니다"를 잠깐 보여준 뒤 카드를 닫는다 — 서버 처리 표시는
        // 보낸 사람 쪽이 남기므로(멱등) 여기서는 화면만 정리한다.
        const name = typeof p.name === "string" && p.name.trim() ? p.name : FALLBACK_USER_NAME;
        setSentNotices((current) => ({ ...current, [alertId]: name }));
        const existing = sentTimers.get(alertId);
        if (existing) clearTimeout(existing);
        sentTimers.set(
          alertId,
          setTimeout(() => {
            sentTimers.delete(alertId);
            setSentNotices((current) => {
              const next = { ...current };
              delete next[alertId];
              return next;
            });
            setCards((current) => current.filter((item) => item.alertId !== alertId));
          }, SENT_NOTICE_CLOSE_MS),
        );
      })
      .subscribe((status) => {
        // 이 콜백이 **연결 감시기**다(티켓 #507). supabase-js 는 접속·재접속마다 SUBSCRIBED 를,
        // 끊김·오류·시간초과마다 다른 값을 주므로, 그걸 그대로 폴링 주기표 선택에 쓴다.
        // 이게 없으면 신호가 죽은 걸 아무도 모른 채 느린 폴링만 남아 문자를 늦게 본다.
        const live = status === "SUBSCRIBED";
        setRealtimeLive(live);
        // (재)접속 직후 한 번 당겨 온다 — 접속이 없던 공백 동안 온 문자를 폴링보다 먼저 잡는다.
        if (live) refetchNow();
      });
    channelRef.current = channel;
    return () => {
      channelRef.current = null;
      setRealtimeLive(false);
      void supabase.removeChannel(channel);
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
      for (const timer of sentTimers.values()) clearTimeout(timer);
      sentTimers.clear();
      setPresence({});
      setSentNotices({});
    };
  }, [allowed, myId, alertScopeKey, scheduleRefetch, refetchNow]);

  // 잠금 신호가 있는 동안만 3초 간격으로 시계를 돌려, "30초 무입력" 판정과 끊긴 하트비트
  // 걷어내기(45초)를 갱신한다. 잠금이 없으면 아무것도 돌지 않는다.
  const hasPresence = Object.keys(presence).length > 0;
  useEffect(() => {
    if (!hasPresence) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setNowTick(now);
      setPresence((current) => {
        const entries = Object.entries(current).filter(
          ([, p]) => now - p.receivedAt <= REPLY_PRESENCE_STALE_MS,
        );
        return entries.length === Object.keys(current).length
          ? current
          : Object.fromEntries(entries);
      });
    }, 3_000);
    return () => clearInterval(timer);
  }, [hasPresence]);

  // 답장 잠금 신호 발신 — 카드들이 부모의 채널 하나를 같이 쓴다(카드마다 채널을 만들지 않는다).
  const sendBroadcast = useCallback((event: string, payload: Record<string, unknown>) => {
    // 실패해도 조용히 넘어간다 — 신호 유실은 하트비트(5초)와 폴링이 메운다.
    void channelRef.current
      ?.send({ type: "broadcast", event, payload })
      .catch(() => undefined);
  }, []);

  // 아직 알림 권한을 물어본 적 없으면 권유 카드를 띄운다(닫으면 이 브라우저에선 다시 안 뜬다).
  useEffect(() => {
    if (!allowed || !notificationsSupported()) return;
    if (Notification.permission !== "default") return;
    if (localStorage.getItem(NOTI_ASK_DISMISSED_KEY) === "1") return;
    setAskNoti(true);
  }, [allowed]);

  function dismissNotiPrompt() {
    localStorage.setItem(NOTI_ASK_DISMISSED_KEY, "1");
    setAskNoti(false);
  }

  useEffect(() => {
    loadedOnceRef.current = false;
    seenAlertIdsRef.current.clear();
    setCards([]);
    setFrontAlertId(null);
  }, [alertScopeKey, allowed]);

  // 알림을 "처리했다"(답장 발송·✕ 닫기)로 서버에 남긴다 — 매장 단위 공유라(티켓 #444) 같은
  // 매장의 다른 사람·다른 PC 카드도 서버 신호(sms-alerts-changed)나 다음 조회 때 사라진다.
  // 화면에 띄우는 순간에는 서버에 알리지 않는다(티켓 #431 — 그 즉시 확인 때문에 먼저 조회한
  // PC 한 대만 알림을 받았다).
  const dismiss = useCallback((alertIds: number[]) => {
    void dismissSmsAlerts(alertIds).catch(() => {
      // 실패해도 이 PC 화면은 이미 치웠고(alertId 기억으로 다시 안 띄운다), 서버엔 미처리로
      // 남아 다른 PC에서 계속 보인다 — 거기서 처리하면 된다.
    });
  }, []);

  useEffect(() => {
    if (!allowed) return;
    if (!query.data) return;
    // 이 응답이 곧 동기화 신호다: ① 아직 안 띄운 알림은 새 카드로 얹고("이미 띄운 알림"은
    // 서버가 아니라 이 브라우저가 alertId 로 기억한다 — 그래서 켜 둔 PC 전부에 뜬다),
    // ② 화면에 있는데 응답에서 빠진 카드는 다른 곳에서 처리된 것이니 함께 치운다.
    const pendingIds = new Set(query.data.items.map((item) => item.alertId));
    const unseen = query.data.items.filter(
      (item) => !seenAlertIdsRef.current.has(item.alertId),
    );
    if (unseen.length > 0) {
      unseen.forEach((item) => seenAlertIdsRef.current.add(item.alertId));
      // 첫 로드는 웹이 닫혀 있던 동안의 묶음일 수 있어 OS 알림 폭탄을 내지 않는다.
      if (loadedOnceRef.current) unseen.forEach(notifyIfAway);
    }
    setCards((current) => [
      // "○○ 님이 답장을 보냈습니다" 안내가 떠 있는 카드는 서버에서 이미 처리됐어도 안내가
      // 끝날 때까지(3초) 남긴다 — 안내 타이머가 스스로 닫는다.
      ...current.filter(
        (item) => pendingIds.has(item.alertId) || sentTimersRef.current.has(item.alertId),
      ),
      ...unseen,
    ]);
    loadedOnceRef.current = true;
  }, [allowed, query.data]);

  if (!allowed || typeof document === "undefined") return null;
  if (cards.length === 0 && !askNoti) return null;

  // 카드는 새것이 앞(맨 위)이다 — 목록 뒤쪽이 최신이므로 뒤집어 그린다.
  const newestFirst = [...cards].reverse();
  // 사람이 뒤에서 꺼내 세운 문자가 있으면 그 한 장만 맨 앞으로 끌어올리고 나머지 순서는 그대로 둔다.
  const pickedIndex =
    frontAlertId == null ? -1 : newestFirst.findIndex((item) => item.alertId === frontAlertId);
  const orderedCards =
    pickedIndex > 0
      ? [newestFirst[pickedIndex], ...newestFirst.filter((_, i) => i !== pickedIndex)]
      : newestFirst;
  // 언제나 맨 앞 한 장만 진짜로 그린다. 나머지는 개수와 한 줄 요약만 있으면 된다.
  const behindCards = orderedCards.slice(1);
  const behindCount = behindCards.length;
  const peekLayers = Math.min(behindCount, STACK_PEEK_LAYERS);
  const previewCards = behindCards.slice(0, STACK_PREVIEW_LIMIT);
  const previewRest = behindCount - previewCards.length;
  const showPreview = stackHovered && behindCount > 0;

  // 한 번에 다 치우기 — 카드가 여러 장이면 하나씩 ✕ 하는 게 고역이다. 문자 자체는 지워지지 않고
  // 수신 메시지함에 그대로 남는다(여기서 없어지는 건 "아직 안 봤다"는 알림뿐).
  const closeAll = () => {
    const ids = cards.map((item) => item.alertId);
    if (ids.length === 0) return;
    dismiss(ids);
    setCards([]);
    setFrontAlertId(null);
  };

  return createPortal(
    <div
      className="fixed bottom-5 right-5 z-[99998] flex w-[min(92vw,22rem)] flex-col gap-2"
      onPointerEnter={() => setStackHovered(true)}
      onPointerLeave={() => setStackHovered(false)}
    >
      {askNoti && <NotificationPrompt onDone={dismissNotiPrompt} />}

      {/* 뒤 카드 미리보기 — 마우스를 올린 동안만 열린다(누를 단추가 따로 없다). 한 줄에
          "누가·언제·무슨 내용"만 담고, 누르면 그 문자가 맨 앞으로 나온다. */}
      {showPreview && (
        <div className="flex flex-col gap-1">
          {previewRest > 0 && (
            <p className="pr-1 text-right text-[11px] font-semibold text-gray-400">
              뒤에 {previewRest}건 더
            </p>
          )}
          {[...previewCards].reverse().map((item) => (
            <button
              key={item.alertId}
              type="button"
              onClick={() => setFrontAlertId(item.alertId)}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-left shadow-md transition hover:border-brand-400 hover:bg-brand-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-brand-500/50 dark:hover:bg-gray-800"
            >
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {item.customerName ? (
                    <MaskedText value={item.customerName} kind="name" />
                  ) : (
                    <MaskedText value={phoneLabel(item)} kind="phone" />
                  )}
                </span>
                <span className="shrink-0 text-[10px] text-gray-400">
                  {receivedLabel(item.receivedAt)}
                </span>
              </span>
              <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
                {item.body.replace(/\s+/g, " ").trim()}
              </span>
            </button>
          ))}
        </div>
      )}

      {orderedCards.slice(0, 1).map((message, index) => (
        <div key={message.alertId} className={index === 0 && peekLayers > 0 && !showPreview ? "relative mt-7" : "relative"}>
          {/* 뒤에 더 있다는 걸 보여 주는 장식 층 — 카드 크기에 딱 맞춰 아래로 조금씩 밀고 좁힌다.
              진짜 카드가 아니라 빈 상자라 답장칸·타이머가 딸려 오지 않는다. */}
          {index === 0 &&
            !showPreview &&
            Array.from({ length: peekLayers }, (_, i) => i + 1)
              .reverse()
              .map((depth) => (
                <div
                  key={`peek-${depth}`}
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-2xl border border-gray-300 bg-white shadow-md dark:border-gray-600 dark:bg-gray-900"
                  style={{
                    // 위로 조금씩 밀고 좁힌다 — 더미가 화면 맨 아래에 붙어 있어서 아래로 밀면
                    // 뒤 층이 화면 밖으로 잘려 "쌓인 느낌"이 아예 안 보인다.
                    transform: `translateY(-${depth * 12}px) scaleX(${1 - depth * 0.06})`,
                  }}
                />
              ))}
          <div className="relative">
          <SmsCard
          message={message}
          sendingDevices={sendingDevices}
          me={{ id: myId, name: myName }}
          lock={presence[message.alertId] ?? null}
          sentBy={sentNotices[message.alertId] ?? null}
          now={nowTick}
          onSendPresence={(draft, idleMs, takenFrom) =>
            sendBroadcast("sms-reply-presence", {
              alertId: message.alertId,
              userId: myId,
              name: myName,
              draft,
              idleMs,
              takenFrom,
            })
          }
          onRelease={() =>
            sendBroadcast("sms-reply-release", { alertId: message.alertId, userId: myId })
          }
          onSent={() =>
            sendBroadcast("sms-reply-sent", {
              alertId: message.alertId,
              userId: myId,
              name: myName,
            })
          }
          onTakeover={() =>
            setPresence((current) => {
              const next = { ...current };
              delete next[message.alertId];
              return next;
            })
          }
          // ✕든 대화창 열기든 답장 후 자동 닫힘이든, 닫히는 순간 "처리됨"을 서버에
          // 남긴다(멱등이라 중복 무해). 대화창은 이 전역 자리에서 열어 보던 화면을 유지한다.
          onClose={() => {
            dismiss([message.alertId]);
            setCards((current) =>
              current.filter((item) => item.alertId !== message.alertId),
            );
          }}
          onOpen={() => {
            rememberInboundDeviceFilter(message.deviceId);
            conversationModal.open({ messageId: `inbound:${message.id}` });
            dismiss([message.alertId]);
            setCards((current) =>
              current.filter((item) => item.alertId !== message.alertId),
            );
          }}
          onHandled={() => dismiss([message.alertId])}
          // 한 번호가 연달아 보낸 문자는 카드가 여러 장이므로, 끄면 그 번호 카드를 모두 치운다.
          // 서버가 그 번호의 알림 원장을 통째로 지우므로(POST /alerts/mutes) 다른 PC 는 다음
          // 조회에서 함께 사라진다 — 여기서 따로 처리 표시를 남길 필요 없다.
          onMuted={(phone) =>
            setCards((current) =>
              current.filter((item) => samePhone(item.sender, phone) === false),
            )
          }
        />
          </div>
        </div>
      ))}

      {/* 조작 줄 — **카드 아래**에 둔다. 더미가 아래쪽에 붙어 있어서 미리보기가 열려도 이 줄은
          제자리에 있다(위에 두면 목록이 자라면서 손 밑에서 밀려나 누를 수가 없었다). */}
      {cards.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          {behindCount > 0 && (
            <span className="rounded-full bg-brand-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
              새 문자 {cards.length}건
            </span>
          )}
          <button
            type="button"
            onClick={closeAll}
            className="rounded-lg bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600 shadow transition hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {cards.length > 1 ? `${cards.length}건 모두 닫기` : "닫기"}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
